import React from 'react';
import { css } from '@emotion/react';
import type { Option } from '@resi-media/resi-ui';
import { Box, Stack, Inline, Text, TabGroup, Tab, Draft } from '@resi-media/resi-ui';
import { JsonEditor } from 'json-edit-react';
import { useForm, Controller } from 'react-hook-form';
import { makeTitleCase } from '@studio/helpers/textCasing/makeTitleCase';
import { determineMockType } from '@preview/helpers/determineMockType';
import { SafeDataObject } from '@preview/helpers/SafeDataObject';
import { useJsonEditorTheme } from '../../hooks/useJsonEditorTheme';
import type { FormValues, MockItem } from '../../types';
import { ChannelMockFields } from '../ChannelMockFields';

type _Props = {
  mock: MockItem;
  onClose: () => void;
  onSubmit: (data: FormValues) => void;
};

const EditMockModal = ({ mock, onClose, onSubmit }: _Props) => {
  const mockType = determineMockType(mock);
  const [currentTab, setTabIndex] = React.useState(0);
  const jsonEditorTheme = useJsonEditorTheme();

  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      mockId: mock.id,
      endpoint: mock.endpoint,
      description: mock.description || '',
      data: JSON.stringify(mock.data, null, 2),
      mockType,
    },
  });

  // Initialize form with mock data
  React.useEffect(() => {
    reset({
      mockId: mock.id,
      endpoint: mock.endpoint,
      description: mock.description || '',
      data: JSON.stringify(mock.data, null, 2),
      mockType,
    });
  }, [mock, reset, mockType]);

  return (
    <React.Fragment>
      <Draft.ModalBody>
        <Stack gap="m">
          <Box
            css={css`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            `}
          >
            <Draft.FormField fieldLabel="Mock Type">
              <Controller
                control={control}
                name="mockType"
                render={({ field }) => {
                  const mockOptions = [
                    { value: 'channel', label: 'Channel' },
                    { value: 'feed', label: 'Feed' },
                    { value: 'playlist', label: 'Playlist' },
                    { value: 'video', label: 'Video' },
                    { value: 'other', label: 'Other' },
                  ];
                  return (
                    <Draft.Select
                      {...field}
                      appendToBody
                      css={css`
                        width: 100%;
                      `}
                      disabled={true} // Can't change type when editing
                      options={mockOptions}
                      value={mockOptions.find((v) => v.value === field.value)}
                    />
                  );
                }}
              />
            </Draft.FormField>

            <Draft.FormField fieldLabel="Endpoint">
              <Controller
                control={control}
                name="endpoint"
                render={({ field }) => (
                  <Draft.TextInput
                    {...field}
                    css={css`
                      width: 100%;
                    `}
                    placeholder="Enter endpoint URL"
                  />
                )}
              />
            </Draft.FormField>
          </Box>

          <Draft.FormField fieldLabel="Description">
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <Draft.TextInput
                  {...field}
                  css={css`
                    width: 100%;
                  `}
                  placeholder="Enter description"
                />
              )}
            />
          </Draft.FormField>

          <Box
            css={css`
              border: 1px solid #f0f0f0;
              border-radius: 4px;
              overflow-y: auto;
            `}
          >
            <TabGroup index={currentTab} isFullWidth onChange={(tabIndex) => setTabIndex(tabIndex)}>
              <Tab label="Fields" />
              <Tab label="JSON" />
            </TabGroup>
            {currentTab === 0 ? (
              <Box
                css={css`
                  padding: 16px;
                  max-height: 400px;
                  overflow-y: auto;
                `}
              >
                <Controller
                  control={control}
                  name="mockType"
                  render={({ field }) => {
                    const mockType = field.value as FormValues['mockType'];
                    const dataValue = watch('data');
                    const dataWrapper = new SafeDataObject(dataValue);

                    if (mockType === 'channel') {
                      return (
                        <ChannelMockFields
                          dataValue={dataValue}
                          onChange={(data: string) => reset({ ...watch(), data })}
                        />
                      );
                    } else if (mockType === 'feed') {
                      return (
                        <Stack gap="s">
                          <Draft.FormField fieldLabel="Name">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                reset({ ...watch(), data: dataWrapper.set('name', e.target.value).toString() });
                              }}
                              value={dataWrapper.get('name', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Description">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                reset({ ...watch(), data: dataWrapper.set('description', e.target.value).toString() });
                              }}
                              value={dataWrapper.get('description', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Channel ID (embed ID for live player)">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setValue('data', dataWrapper.set('channelId', e.target.value).toString());
                              }}
                              placeholder="Leave empty to disable live player"
                              value={dataWrapper.get('channelId', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Branding Mode">
                            <Draft.Select<Option>
                              appendToBody
                              onChange={(value: Option) => {
                                reset({
                                  ...watch(),
                                  data: dataWrapper
                                    .set('branding', { ...dataWrapper.get('branding', {}), mode: value.value })
                                    .toString(),
                                });
                              }}
                              options={[
                                { value: 'light', label: 'Light' },
                                { value: 'dark', label: 'Dark' },
                              ]}
                              value={{
                                value: dataWrapper.getNested('branding', 'mode', 'light'),
                                label: makeTitleCase(dataWrapper.getNested('branding', 'mode', 'light')),
                              }}
                            />
                          </Draft.FormField>
                          <Text>Playlists data can be edited in JSON mode</Text>
                        </Stack>
                      );
                    } else if (mockType === 'playlist') {
                      const dataWrapper = new SafeDataObject(dataValue);
                      return (
                        <Stack gap="s">
                          <Draft.FormField fieldLabel="ID">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updatedWrapper = dataWrapper.set('id', e.target.value);
                                reset({
                                  ...watch(),
                                  data: updatedWrapper.toString(),
                                });
                              }}
                              value={dataWrapper.get('id', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Name">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updatedWrapper = dataWrapper.set('name', e.target.value);
                                reset({
                                  ...watch(),
                                  data: updatedWrapper.toString(),
                                });
                              }}
                              value={dataWrapper.get('name', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Description">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updatedWrapper = dataWrapper.set('description', e.target.value);
                                reset({
                                  ...watch(),
                                  data: updatedWrapper.toString(),
                                });
                              }}
                              value={dataWrapper.get('description', '')}
                            />
                          </Draft.FormField>
                          <Text>Videos data can be edited in JSON mode</Text>
                        </Stack>
                      );
                    } else if (mockType === 'video') {
                      const dataWrapper = new SafeDataObject(dataValue);
                      return (
                        <Stack gap="s">
                          <Draft.FormField fieldLabel="ID">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updatedWrapper = dataWrapper.set('id', e.target.value);
                                reset({
                                  ...watch(),
                                  data: updatedWrapper.toString(),
                                });
                              }}
                              value={dataWrapper.get('id', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Title">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updatedWrapper = dataWrapper.set('title', e.target.value);
                                reset({
                                  ...watch(),
                                  data: updatedWrapper.toString(),
                                });
                              }}
                              value={dataWrapper.get('title', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Description">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updatedWrapper = dataWrapper.set('description', e.target.value);
                                reset({
                                  ...watch(),
                                  data: updatedWrapper.toString(),
                                });
                              }}
                              value={dataWrapper.get('description', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Thumbnail URL">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('thumbnail', {
                                  ...dataWrapper.get('thumbnail', {}),
                                  url: e.target.value,
                                });
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.getNested('thumbnail', 'url', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Duration (seconds)">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('duration', Number(e.target.value));
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              type="number"
                              value={dataWrapper.get('duration', 0)}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Tags (comma-separated)">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set(
                                  'tags',
                                  e.target.value.split(',').map((tag: string) => tag.trim())
                                );
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.get('tags', []).join(', ')}
                            />
                          </Draft.FormField>
                        </Stack>
                      );
                    } else {
                      return (
                        <Text>This mock type doesn't have specialized fields. Please use JSON mode to edit data.</Text>
                      );
                    }
                  }}
                />
              </Box>
            ) : (
              <Box>
                <Controller
                  control={control}
                  name="data"
                  render={({ field }) => {
                    const dataWrapper = new SafeDataObject(field.value);

                    return (
                      <JsonEditor
                        data={dataWrapper.toJSON()}
                        indent={1}
                        keySort
                        maxWidth="100%"
                        rootName="data"
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        setData={(data: any) => {
                          try {
                            field.onChange(JSON.stringify(data, null, 2));
                          } catch (error) {
                            console.error('Error updating JSON:', error);
                          }
                        }}
                        showCollectionCount="when-closed"
                        theme={jsonEditorTheme}
                      />
                    );
                  }}
                />
              </Box>
            )}
          </Box>
        </Stack>
      </Draft.ModalBody>
      <Draft.ModalFooter>
        <Inline gap="md" justifyContent="flex-end">
          <Draft.Button label="Cancel" onClick={onClose}>
            Cancel
          </Draft.Button>
          <Draft.Button label="Save" onClick={handleSubmit(onSubmit)} variant="contained">
            Save
          </Draft.Button>
        </Inline>
      </Draft.ModalFooter>
    </React.Fragment>
  );
};

export default EditMockModal;
